import { useMemo, useState } from 'react';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Tabs, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDateTime, formatNumber } from '../lib/format';
import { printList, printQuantity } from '../lib/print';
import { IconBox, IconPrint, IconTruck, IconUpload } from '../components/icons';
import { ArticleImportModal } from './material/ArticleImportModal';
import { InventoryPanel } from './material/InventoryPanel';
import { StockControlPanel } from './material/StockControlPanel';
import { TransferModal } from './material/TransferModal';

type MaterialTab = 'artikel' | 'bestand' | 'bewegungen' | 'kontrolle' | 'inventur';

export function MaterialPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [tab, setTab] = useState<MaterialTab>('kontrolle');
  const [search, setSearch] = useState('');
  const [articleModal, setArticleModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [adjustArticle, setAdjustArticle] = useState<any>(null);
  const [transferArticle, setTransferArticle] = useState<any>(null);

  const articles = useAsync(() => api.get('/api/material/articles', { search }), [search, tab]);
  const warehouses = useAsync(() => api.get('/api/material/warehouses'), []);
  const stock = useAsync(() => api.get('/api/material/stock'), [tab]);
  const movements = useAsync(() => (tab === 'bewegungen' ? api.get('/api/material/movements') : Promise.resolve([])), [tab]);

  const belowMin = (articles.data ?? []).filter((article: any) => article.total_stock < article.min_stock);

  /**
   * Welcher Artikel liegt in welchem Fahrzeug?
   *
   * Aus den ohnehin geladenen Beständen abgeleitet statt je Zeile nachgefragt –
   * die Frage „steckt das noch im Auto?“ soll die Artikelliste beantworten,
   * ohne sie zu verlangsamen.
   */
  const inVehicles = useMemo(() => {
    const map = new Map<number, { name: string; quantity: number }[]>();
    for (const row of stock.data ?? []) {
      if (row.kind !== 'vehicle' || !row.quantity) continue;
      const entries = map.get(row.article_id) ?? [];
      // Das Kennzeichen zuerst: Danach wird im Lager gefragt, nicht nach dem
      // Namen des Fahrzeuglagers. Ohne verknüpftes Fahrzeug bleibt der Name.
      entries.push({ name: row.vehicle_plate ?? row.warehouse_name, quantity: row.quantity });
      map.set(row.article_id, entries);
    }
    return map;
  }, [stock.data]);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBox width={22} height={22} />}
        title="Material & Lagerwirtschaft"
        subtitle="Artikelstamm, Bestände je Lagerort (Zentrallager, Fahrzeug- und Objektlager) sowie sämtliche Bestandsbewegungen. Unterschreitet ein Artikel den Mindestbestand, entsteht automatisch ein Bestellvorschlag."
        pill="Lagerwirtschaft"
        tone="warning"
        actions={
          can('material.articles') ? (
            <>
              <button className="btn" onClick={() => setImportModal(true)}>
                <IconUpload width={14} height={14} /> Importieren
              </button>
              <button className="btn primary" onClick={() => setArticleModal(true)}>
                Artikel anlegen
              </button>
            </>
          ) : undefined
        }
      />

      {belowMin.length > 0 && (
        <div className="alert warning">
          <strong>{belowMin.length} Artikel</strong> liegen unter dem Mindestbestand:{' '}
          {belowMin.slice(0, 4).map((article: any) => article.name).join(', ')}
          {belowMin.length > 4 ? ' …' : ''}
        </div>
      )}

      <Card noBody>
        <div className="card-body tight" style={{ paddingBottom: 0 }}>
          <Tabs
            active={tab}
            onChange={setTab}
            tabs={[
              { key: 'kontrolle', label: 'Bestandsübersicht' },
              { key: 'bestand', label: 'Lagerbestände' },
              { key: 'bewegungen', label: 'Lagerbewegungen' },
              { key: 'artikel', label: 'Artikelstamm', count: articles.data?.length },
              { key: 'inventur', label: 'Inventur' },
            ]}
          />
          {(tab === 'artikel' || tab === 'bestand') && (
            <div className="row between" style={{ marginBottom: 12 }}>
              {tab === 'artikel' ? (
                <input
                  type="search"
                  placeholder="Artikel, Nummer oder Barcode suchen …"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  style={{ maxWidth: 360 }}
                />
              ) : (
                <span className="small subtle">Bestände je Lagerort, Fahrzeuglager eingeschlossen.</span>
              )}
              <button
                className="btn sm"
                onClick={() =>
                  tab === 'artikel'
                    ? printArticles(articles.data ?? [], search, inVehicles)
                    : printStock(stock.data ?? [])
                }
              >
                <IconPrint width={14} height={14} /> Liste drucken
              </button>
            </div>
          )}
        </div>

        {tab === 'artikel' &&
          (articles.loading ? (
            <Loading />
          ) : !articles.data?.length ? (
            <EmptyState title="Keine Artikel gefunden" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Nummer</th>
                    <th>Bezeichnung</th>
                    <th>Kategorie</th>
                    <th>Lieferant</th>
                    <th className="num">EK-Preis</th>
                    <th className="num">Bestand</th>
                    <th className="num">Mindest</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {articles.data.map((article: any) => {
                    const vehicles = inVehicles.get(article.id) ?? [];
                    return (
                      <tr key={article.id}>
                        <td className="mono small">
                          {article.article_no}
                        </td>
                        <td>
                          <strong>{article.name}</strong>
                          {article.barcode && <div className="small subtle mono">{article.barcode}</div>}
                          {vehicles.length > 0 && (
                            <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                              {vehicles.map((vehicle) => (
                                <span
                                  key={vehicle.name}
                                  className="chip tiny tone-warning"
                                  title={`${formatNumber(vehicle.quantity, 2)} ${article.unit} in ${vehicle.name}`}
                                >
                                  <IconTruck width={12} height={12} />
                                  {/* Der Zusatz „Fahrzeug“ steht schon im Symbol. */}
                                  {vehicle.name.replace(/^Fahrzeug\s+/i, '')} · {formatNumber(vehicle.quantity, 2)}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="small">{article.category ?? '–'}</td>
                        <td className="small">{article.supplier_name ?? '–'}</td>
                        <td className="num">{formatCurrency(article.purchase_price)}</td>
                        <td className="num">
                          <Badge variant={article.total_stock < article.min_stock ? 'danger' : 'success'}>
                            {formatNumber(article.total_stock, 2)} {article.unit}
                          </Badge>
                        </td>
                        <td className="num small subtle">{formatNumber(article.min_stock, 2)}</td>
                        <td className="actions">
                          {can('material.book') && (
                            <button
                              className="btn sm"
                              onClick={() => setTransferArticle(article)}
                              title="Zwischen Lagern umbuchen, z. B. ins Fahrzeug"
                            >
                              Umbuchen
                            </button>
                          )}
                          {can('material.inventory') && (
                            <button className="btn sm" onClick={() => setAdjustArticle(article)}>
                              Korrigieren
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

        {tab === 'bestand' &&
          (stock.loading ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Artikel</th>
                    <th>Lagerort</th>
                    <th className="num">Menge</th>
                    <th className="num">Gesamtbestand</th>
                    <th className="num">Wert</th>
                  </tr>
                </thead>
                <tbody>
                  {(stock.data ?? [])
                    .filter((row: any) => row.warehouse_id)
                    .map((row: any, index: number) => (
                      <tr key={`${row.article_id}-${row.warehouse_id}-${index}`}>
                        <td>
                          {row.name}
                          <div className="small subtle mono">{row.article_no}</div>
                        </td>
                        <td className="small">
                          <span className="row" style={{ gap: 6 }}>
                            {row.kind === 'vehicle' && <IconTruck width={13} height={13} />}
                            {row.vehicle_plate ? `${row.vehicle_plate} · ${row.warehouse_name}` : row.warehouse_name}
                            <Badge variant={row.kind === 'vehicle' ? 'warning' : 'info'}>{warehouseKind(row.kind)}</Badge>
                          </span>
                        </td>
                        <td className="num">
                          {formatNumber(row.quantity, 2)} {row.unit}
                        </td>
                        <td className="num small subtle">{formatNumber(row.total_quantity, 2)}</td>
                        <td className="num">{formatCurrency(row.quantity * row.purchase_price)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))}

        {/* Die beiden Auswertungsreiter bringen Kacheln und Formulare mit,
            keine randlose Tabelle. Sie brauchen deshalb den Innenabstand der
            Karte – sonst kleben die Kacheln am Rahmen, während die
            Reiterleiste darüber eingerückt sitzt. */}
        {tab === 'kontrolle' && (
          <div className="card-body">
            <StockControlPanel />
          </div>
        )}
        {tab === 'inventur' && (
          <div className="card-body">
            <InventoryPanel warehouses={warehouses.data ?? []} />
          </div>
        )}

        {tab === 'bewegungen' &&
          (movements.loading ? (
            <Loading />
          ) : !movements.data?.length ? (
            <EmptyState title="Keine Bewegungen" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>Artikel</th>
                    <th>Lagerort</th>
                    <th className="num">Menge</th>
                    <th>Grund</th>
                    <th>Auftrag</th>
                    <th>Person</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.data.map((movement: any) => (
                    <tr key={movement.id}>
                      <td className="small">
                        {formatDateTime(movement.created_at)}
                      </td>
                      <td className="small">{movement.article_name}</td>
                      <td className="small">{movement.warehouse_name}</td>
                      <td className="num">
                        <span style={{ color: movement.quantity < 0 ? 'var(--danger-text)' : 'var(--success-text)' }}>
                          {movement.quantity > 0 ? '+' : ''}
                          {formatNumber(movement.quantity, 2)}
                        </span>
                      </td>
                      <td>
                        <Badge>{reasonLabel(movement.reason)}</Badge>
                      </td>
                      <td className="mono small">{movement.task_no ?? '–'}</td>
                      <td className="small">
                        {movement.first_name ? `${movement.first_name} ${movement.last_name}` : 'System'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>

      {articleModal && (
        <ArticleModal
          onClose={() => setArticleModal(false)}
          onSaved={() => {
            setArticleModal(false);
            articles.reload();
            notify('Artikel angelegt.', 'success');
          }}
        />
      )}

      {importModal && (
        <ArticleImportModal
          onClose={() => setImportModal(false)}
          onImported={() => {
            articles.reload();
            stock.reload();
          }}
        />
      )}

      {transferArticle && (
        <TransferModal
          article={transferArticle}
          warehouses={warehouses.data ?? []}
          onClose={() => setTransferArticle(null)}
          onSaved={() => {
            setTransferArticle(null);
            articles.reload();
            stock.reload();
            notify('Umbuchung gebucht.', 'success');
          }}
        />
      )}

      {adjustArticle && (
        <AdjustModal
          article={adjustArticle}
          warehouses={warehouses.data ?? []}
          onClose={() => setAdjustArticle(null)}
          onSaved={() => {
            setAdjustArticle(null);
            articles.reload();
            stock.reload();
            notify('Bestand korrigiert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function ArticleModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const suppliers = useAsync(() => api.get('/api/procurement/suppliers').catch(() => []), []);
  const [form, setForm] = useState({
    articleNo: '',
    name: '',
    unit: 'Stk',
    category: '',
    barcode: '',
    purchasePrice: '0',
    minStock: '0',
    targetStock: '0',
    supplierId: '',
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title="Artikel anlegen"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.articleNo || form.name.length < 2}
            onClick={async () => {
              try {
                await api.post('/api/material/articles', {
                  ...form,
                  purchasePrice: Number(form.purchasePrice),
                  minStock: Number(form.minStock),
                  targetStock: Number(form.targetStock),
                  supplierId: form.supplierId ? Number(form.supplierId) : null,
                });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Artikelnummer" required>
            <input value={form.articleNo} onChange={(event) => set('articleNo', event.target.value)} autoFocus />
          </Field>
          <Field label="Bezeichnung" required>
            <input value={form.name} onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field label="Einheit">
            <input value={form.unit} onChange={(event) => set('unit', event.target.value)} />
          </Field>
          <Field label="Kategorie">
            <input value={form.category} onChange={(event) => set('category', event.target.value)} />
          </Field>
          <Field label="Barcode">
            <input value={form.barcode} onChange={(event) => set('barcode', event.target.value)} />
          </Field>
          <Field label="Einkaufspreis (€)">
            <input type="number" step="0.01" value={form.purchasePrice} onChange={(event) => set('purchasePrice', event.target.value)} />
          </Field>
          <Field label="Mindestbestand">
            <input type="number" step="1" value={form.minStock} onChange={(event) => set('minStock', event.target.value)} />
          </Field>
          <Field label="Sollbestand">
            <input type="number" step="1" value={form.targetStock} onChange={(event) => set('targetStock', event.target.value)} />
          </Field>
          <Field label="Lieferant">
            <SearchSelect
              value={String(form.supplierId ?? '')}
              onChange={(wert) => set('supplierId', wert)}
              options={(suppliers.data ?? []).map((supplier: any) => ({ value: String(supplier.id), label: String(supplier.name) }))}
              placeholder="Ohne"
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function AdjustModal({
  article,
  warehouses,
  onClose,
  onSaved,
}: {
  article: any;
  warehouses: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [warehouseId, setWarehouseId] = useState(String(warehouses[0]?.id ?? ''));
  const [quantity, setQuantity] = useState('0');
  const [mode, setMode] = useState<'delta' | 'set'>('delta');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title={`Bestand korrigieren – ${article.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            onClick={async () => {
              try {
                await api.post('/api/material/stock/adjust', {
                  articleId: article.id,
                  warehouseId: Number(warehouseId),
                  quantity: Number(quantity),
                  mode,
                  note,
                });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Buchen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Lagerort" required>
          <SearchSelect
            value={String(warehouseId ?? '')}
            onChange={(wert) => setWarehouseId(wert)}
            options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
            clearable={false}
          />
        </Field>
        <Field label="Art der Korrektur">
          <select value={mode} onChange={(event) => setMode(event.target.value as 'delta' | 'set')}>
            <option value="delta">Zu-/Abgang buchen</option>
            <option value="set">Bestand auf Wert setzen (Inventur)</option>
          </select>
        </Field>
        <Field label="Menge" required>
          <input type="number" step="0.01" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </Field>
        <Field label="Bemerkung">
          <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="z. B. Inventur 08/2026" />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Artikelstammliste.
 *
 * Der Fahrzeugbestand steht als eigene Spalte darin – auf Papier lässt sich
 * kein Tooltip anfassen, und im Lager ist genau das die Frage, die vor der
 * Nachbestellung steht.
 */
function printArticles(articles: any[], search: string, inVehicles: Map<number, { name: string; quantity: number }[]>) {
  const value = articles.reduce((sum, article) => sum + article.total_stock * article.purchase_price, 0);

  printList({
    title: 'Artikelstamm',
    subtitle: search ? `gefiltert nach „${search}“` : 'alle geführten Artikel',
    orientation: 'landscape',
    meta: [
      { label: 'Artikel', value: String(articles.length) },
      { label: 'Bestandswert', value: formatCurrency(value) },
      { label: 'Unter Mindestbestand', value: String(articles.filter((a) => a.total_stock < a.min_stock).length) },
    ],
    columns: [
      { label: 'Nummer', value: (a: any) => a.article_no },
      { label: 'Bezeichnung', value: (a: any) => a.name, emphasis: (a: any) => (a.total_stock < a.min_stock ? 'warning' : null) },
      { label: 'Kategorie', value: (a: any) => a.category ?? '–' },
      { label: 'Lieferant', value: (a: any) => a.supplier_name ?? '–' },
      { label: 'EK-Preis', value: (a: any) => formatCurrency(a.purchase_price), align: 'right' },
      { label: 'Bestand', value: (a: any) => printQuantity(a.total_stock, a.unit), align: 'right' },
      { label: 'Mindest', value: (a: any) => formatNumber(a.min_stock, 2), align: 'right' },
      {
        label: 'davon in Fahrzeugen',
        value: (a: any) =>
          (inVehicles.get(a.id) ?? []).map((vehicle) => `${vehicle.name}: ${vehicle.quantity}`).join(', ') || '–',
      },
    ],
    rows: articles,
    footer: [{ label: `Bestandswert über ${articles.length} Artikel`, value: formatCurrency(value) }],
  });
}

/** Bestandsliste je Lagerort – die Zeilen sind bereits nach Lager sortiert. */
function printStock(stock: any[]) {
  const rows = stock.filter((row: any) => row.warehouse_id);
  const value = rows.reduce((sum, row) => sum + row.quantity * row.purchase_price, 0);

  printList({
    title: 'Bestandsliste',
    subtitle: 'Bestände je Lagerort',
    meta: [
      { label: 'Positionen', value: String(rows.length) },
      { label: 'Gesamtwert', value: formatCurrency(value) },
    ],
    columns: [
      { label: 'Nummer', value: (row: any) => row.article_no },
      { label: 'Artikel', value: (row: any) => row.name },
      {
        label: 'Lagerort',
        value: (row: any) =>
          row.vehicle_plate
            ? `${row.warehouse_name} (${row.vehicle_plate})`
            : `${row.warehouse_name} (${warehouseKind(row.kind)})`,
        emphasis: (row: any) => (row.kind === 'vehicle' ? 'muted' : null),
      },
      { label: 'Menge', value: (row: any) => printQuantity(row.quantity, row.unit), align: 'right' },
      { label: 'Gesamt', value: (row: any) => printQuantity(row.total_quantity), align: 'right' },
      { label: 'Wert', value: (row: any) => formatCurrency(row.quantity * row.purchase_price), align: 'right' },
    ],
    rows,
    footer: [{ label: `Gesamtwert über ${rows.length} Position(en)`, value: formatCurrency(value) }],
  });
}

function warehouseKind(kind: string): string {
  return { central: 'Zentrallager', vehicle: 'Fahrzeug', property: 'Objektlager' }[kind] ?? kind;
}

function reasonLabel(reason: string): string {
  return (
    {
      consumption: 'Verbrauch',
      return: 'Rückgabe',
      receipt: 'Wareneingang',
      transfer: 'Umlagerung',
      inventory: 'Inventur',
    }[reason] ?? reason
  );
}
